<script lang="ts">
	import { cart } from '$lib/store/cart.store';
	import { derived } from 'svelte/store';
	import { languageStore } from '$lib/store/language.store';
	import type { ShippingRatesResponse } from '$lib/types/shipping-rate';
	import { T } from '@tolgee/svelte';
	import ProductPrice from '$lib/components/Product/ProductPrice.svelte';
	import { CurrencyCode } from '$houdini';
	import { sumMoney } from '$lib/utils';
	import { browser } from '$app/environment';

	$: costData = $cart.data?.cost;
	$: totalDiscount =
		Number(costData?.subtotalAmount.amount ?? 0) +
		Number(costData?.totalTaxAmount?.amount ?? 0) -
		Number(costData?.totalAmount.amount ?? 0);

	$: activeShippingRatePromise = browser
		? fetch(`/api/shipping/${$languageStore.country}`)
				.then(
					(res) =>
						res.json() as Promise<
							ShippingRatesResponse['shipping_zones'][number]['price_based_shipping_rates']
						>
				)
				.then((shippingRates) => {
					if (costData?.totalAmount?.amount && shippingRates) {
						let maxShippingRate = shippingRates.find(
							(r) =>
								r.min_order_subtotal &&
								parseFloat(r.min_order_subtotal) <= Number(costData?.totalAmount?.amount || '0')
						);
						let fallbackShippingRate = shippingRates.find(
							(r) =>
								r.max_order_subtotal &&
								parseFloat(r.max_order_subtotal) >= Number(costData?.totalAmount?.amount || '0')
						);
						return maxShippingRate || fallbackShippingRate;
					}
					return null;
				})
		: Promise.resolve();
</script>

<div class="space-y-3 pb-3">
	{#if costData?.totalAmount}
		<p class="flex justify-between">
			<span>
				<T keyName="checkout.shippingCost" />
			</span>

			<span>
				{#await activeShippingRatePromise}
					<T keyName="checkout.shippingCost.free" />
				{:then activeShippingRate}
					{#if !activeShippingRate || activeShippingRate?.price === '0.00'}
						<T keyName="checkout.shippingCost.free" />
					{:else}
						<ProductPrice
							price={{
								amount: Number(activeShippingRate.price),
								currencyCode: costData?.totalAmount?.currencyCode ?? CurrencyCode.EUR
							}}
						/>
					{/if}
				{/await}
			</span>
		</p>
	{:else}
		<div class="h-6"></div>
	{/if}

	{#if Math.round(totalDiscount) !== 0}
		<div class="flex justify-between">
			<span>
				<T keyName="checkout.discount" />
			</span>

			<div>
				<ProductPrice
					price={{
						amount: totalDiscount,
						currencyCode: costData?.totalAmount?.currencyCode ?? CurrencyCode.EUR
					}}
				/>
			</div>
		</div>
	{/if}

	<hr class="bg-dark-black h-0.5 border-none" />

	<div class="flex justify-between">
		<span class="flex flex-col">
			<span class="font-semibold text-h-16">
				<T keyName="checkout.totalSum" />
			</span>

			<span class="text-p-12">
				<T keyName="checkout.inclVAT" />
			</span>
		</span>

		<div class="text-h-24">
			<ProductPrice
				price={sumMoney(costData?.totalAmount, costData?.totalTaxAmount) ?? {
					amount: 0,
					currencyCode: 'EUR'
				}}
			/>
		</div>
	</div>
</div>
